<template>
  <v-container fluid>
    <v-row>
      <v-col>
        <v-text-field
          v-model="cName"
          :label="$t('project.name')"
          :error="projectNameError"
          clearable
        />
      </v-col>
    </v-row>
    <v-row>
      <v-col sm="3">
        <v-row>
          <v-col class="py-0 pr-0">
            <v-text-field
              v-model="cWidth"
              :label="$t('project.width')"
              clearable
              type="number"
              min="0"
              max="9999"
              @input="pWidthChanged"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col class="py-0 pr-0">
            <v-text-field
              v-model="cHeight"
              :label="$t('project.height')"
              clearable
              type="number"
              min="0"
              max="9999"
              @input="pHeightChanged"
            />
          </v-col>
        </v-row>
      </v-col>
      <v-col
        class="px-1 pt-4"
        sm="2"
      >
        <v-btn
          id="ratio-lock"
          text
          class="px-0 mx-0"
          @click="toggleRatioLock()"
        >
          <v-img
            :src="require('../../assets/icon/dialog_new_project_ratio_lock.png')"
            max-width="18px"
            max-height="83px"
            min-width="18px"
            min-height="83px"
            aspect-ratio="1"
          />
          <v-icon>{{ ratioLock }}</v-icon>
        </v-btn>
      </v-col>
      <v-col
        class="pl-1"
        sm="7"
      >
        <v-row>
          <v-col class="py-0">
            <v-overflow-btn
              v-model="pResolution"
              dense
              :items="cResolution"
              :label="$t('project.size-preset')"
              @change="resolutionChanged"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col class="py-0">
            <v-btn
              block
              @click="invertWidthHeight"
            >
              <v-icon class="mr-2">
                {{ cOrientationIcon }}
              </v-icon>
              {{ $t("orientation." + cOrientationName) }}
            </v-btn>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
export default {
  data() {
    return {
      pLock: false,
      pResolution: 0,
      pRatio: 16 / 9,
      pWidth: "",
      pName: "",
      pHeight: "",
      projectNameError: false,
      ratioLock: "mdi-lock-open-outline",
      resolutions: {
        landscape: [
          "4K (3840 x 2160)",
          "Full HD (1920 x 1080)",
          "LED p3.9 6m² (768 x 512)",
          "LED p3.9 3m² (512 x 384)",
          "LED p3.9 2.18m² (448 x 320)",
          "LED p3.9 1.5m² (384 x 256)",
          "LED p5.9 6m² (504 x 336)",
          "LED p5.9 3m² (336 x 252)",
          "LED p5.9 2.18m² (294 x 210)",
          "LED p5.9 1.5m² (252 x 168)",
        ],
        portrait: [
          "4K (2160 x 3840)",
          "Full HD (1080 x 1920)",
          "LED p3.9 6m² (512 x 768)",
          "LED p3.9 3m² (384 x 512)",
          "LED p3.9 2.18m² (320 x 448)",
          "LED p3.9 1.5m² (256 x 384)",
          "LED p5.9 6m² (336 x 504)",
          "LED p5.9 3m² (252 x 336)",
          "LED p5.9 2.18m² (210 x 294)",
          "LED p5.9 1.5m² (168 x 252)",
        ],
      },
    };
  },
  computed: {
    cName: {
      get() {
        return this.pName;
      },
      set(value) {
        if (this.isValidFilename(value)) {
          this.pName = value;
          this.projectNameError = false;
          this.$emit("nameUpdated", this.pName);
          return;
        }
        this.projectNameError = true;
      },
    },
    cWidth: {
      get() {
        return this.pWidth;
      },
      set(value) {
        this.pWidth = parseInt(value);
        this.$emit("sizeUpdated", { width: this.pWidth, height: this.pHeight });
      },
    },
    cHeight: {
      get() {
        return this.pHeight;
      },
      set(value) {
        this.pHeight = parseInt(value);
        this.$emit("sizeUpdated", { width: this.pWidth, height: this.pHeight });
      },
    },
    cOrientationName: {
      get() {
        if (this.cWidth >= this.cHeight) {
          return "landscape";
        } else {
          return "portrait";
        }
      },
    },
    cOrientationIcon: {
      get() {
        if (this.cWidth >= this.cHeight) {
          return "mdi-phone-rotate-portrait";
        } else {
          return "mdi-phone-rotate-landscape";
        }
      },
    },
    cResolution: {
      get() {
        return this.resolutions[this.cOrientationName];
      },
    },
  },
  watch: {
    pResolution: function (val) {
      if (val == 0) {
        this.unlockRatio();
      } else {
        this.lockRatio();
      }
    },
  },
  created() {
    //
  },
  methods: {
    isValidFilename(fname) {
      // eslint-disable-next-line no-useless-escape
      const rg1 = /^[^\\\/\:\*\?\"\'\<\>\|\_\.\+\&\!\#\%]+$/; // forbidden characters \ / : * ? " < > | _ . + & ' ! # %
      const rg2 = /^\./; // cannot start with dot (.)
      const rg3 = /^(nul|prn|con|lpt[0-9]|com[0-9])(\.|$)/i; // forbidden file names
      return rg1.test(fname) && !rg2.test(fname) && !rg3.test(fname);
    },
    invertWidthHeight() {
      this.pResolution = 0;

      const currentWidth = this.cWidth;
      const currentHeight = this.cHeight;

      this.cWidth = currentHeight;
      this.cHeight = currentWidth;
    },
    toggleRatioLock() {
      if (this.pLock) {
        this.unlockRatio();
      } else {
        this.lockRatio();
      }
    },
    lockRatio() {
      this.ratioLock = "mdi-lock";
      this.pLock = true;
    },
    unlockRatio() {
      this.ratioLock = "mdi-lock-open-outline";
      this.pLock = false;
    },
    pWidthChanged() {
      if (this.cWidth > 0) {
        if (this.pLock) {
          this.pHeight = Math.round(this.cWidth / this.pRatio);
        }
        this.cWidth = parseInt(this.cWidth);
      }
      this.$emit("sizeUpdated", { width: this.cWidth, height: this.pHeight });
    },
    pHeightChanged() {
      if (this.cHeight > 0) {
        if (this.pLock) {
          this.cWidth = Math.round(this.cHeight * this.pRatio);
        }
        this.cHeight = parseInt(this.cHeight);
      }
      this.$emit("sizeUpdated", { width: this.cWidth, height: this.cHeight });
    },
    resolutionChanged() {
      const completeResolution = this.pResolution.split("(")[1].replace(")", "");
      const dimensions = completeResolution.split(" x ");

      this.cWidth = dimensions[0];
      this.cHeight = dimensions[1];

      if (this.cHeight > 0) {
        this.pRatio = this.cWidth / this.cHeight;
      } else {
        this.pRatio = this.cWidth / 1;
      }
    },
  },
};
</script>

<style>
#ratio-lock {
  width: 50px;
  height: 100px;
}
</style>
